import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './IntroAnimation.css';

const IntroAnimation = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [showContent, setShowContent] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Start showing content after a brief delay
    const contentTimer = setTimeout(() => {
      setShowContent(true);
    }, 300);

    // Auto-progress loading bar
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 2;
      });
    }, 30);

    return () => {
      clearTimeout(contentTimer);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    // Auto-complete after progress reaches 100%
    if (progress >= 100) {
      const timer = setTimeout(() => {
        handleSkip();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [progress]);

  const handleSkip = () => {
    setIsExiting(true);
    setTimeout(() => {
      onComplete();
    }, 800);
  };

  return (
    <AnimatePresence>
      {!isExiting && (
        <motion.div
          className="intro-animation"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.1 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
          data-testid="intro-animation"
        >
          {/* Animated Background */}
          <div className="intro-background">
            <div className="grid-overlay" />
            <div className="gradient-orb orb-1" />
            <div className="gradient-orb orb-2" />
            <div className="gradient-orb orb-3" />
          </div>

          {/* Particle Effects */}
          <div className="particles">
            {[...Array(30)].map((_, i) => (
              <motion.div
                key={i}
                className="particle"
                initial={{
                  x: Math.random() * window.innerWidth,
                  y: Math.random() * window.innerHeight,
                  opacity: 0,
                }}
                animate={{
                  y: [null, -window.innerHeight],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: Math.random() * 3 + 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                  ease: 'linear',
                }}
              />
            ))}
          </div>

          {/* Main Content */}
          {showContent && (
            <div className="intro-content">
              {/* Logo with Glitch Effect */}
              <motion.div
                className="intro-logo-container"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                <motion.h1
                  className="intro-logo"
                  animate={{
                    textShadow: [
                      '0 0 20px rgba(0, 212, 255, 0.5)',
                      '0 0 40px rgba(0, 212, 255, 0.8)',
                      '0 0 20px rgba(0, 212, 255, 0.5)',
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  DANSTON<span className="logo-ai-intro">AI</span>
                </motion.h1>
                
                {/* Glitch Layers */}
                <div className="glitch-layer layer-1">
                  DANSTON<span className="logo-ai-intro">AI</span>
                </div>
                <div className="glitch-layer layer-2">
                  DANSTON<span className="logo-ai-intro">AI</span>
                </div>
              </motion.div>

              {/* Tagline with Typing Effect */}
              <motion.div
                className="intro-tagline"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
              >
                <TypeWriter text="AI That Works For You. 24x7." delay={0.5} />
              </motion.div>

              {/* Loading Bar */}
              <motion.div
                className="loading-container"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
              >
                <div className="loading-bar-bg">
                  <motion.div
                    className="loading-bar-fill"
                    style={{ width: `${progress}%` }}
                  />
                  <motion.div
                    className="loading-bar-glow"
                    style={{ left: `${progress}%` }}
                  />
                </div>
                <div className="loading-text">
                  {progress < 100 ? `Loading ${Math.floor(progress)}%` : 'Ready'}
                </div>
              </motion.div>

              {/* Skip Button */}
              <motion.button
                className="skip-button"
                onClick={handleSkip}
                initial={{ opacity: 0 }}
                animate={{ opacity: progress >= 100 ? 1 : 0.6 }}
                whileHover={{ scale: 1.05, opacity: 1 }}
                whileTap={{ scale: 0.95 }}
                transition={{ delay: 1 }}
              >
                {progress >= 100 ? 'Enter' : 'Skip'}
              </motion.button>
            </div>
          )}

          {/* Scanline Effect */}
          <div className="scanline" />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// TypeWriter Component
const TypeWriter = ({ text, delay = 0 }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const startTimer = setTimeout(() => {
      if (currentIndex < text.length) {
        const timer = setTimeout(() => {
          setDisplayText((prev) => prev + text[currentIndex]);
          setCurrentIndex((prev) => prev + 1);
        }, 80);
        return () => clearTimeout(timer);
      }
    }, delay * 1000);

    return () => clearTimeout(startTimer);
  }, [currentIndex, text, delay]);

  return (
    <span className="typewriter-text">
      {displayText}
      {currentIndex < text.length && <span className="cursor">|</span>}
    </span>
  );
};

export default IntroAnimation;
